import type {
  CarrierDiscountRuleStatus,
  CarrierLabelDiscountRule,
} from "@/api/carrier";

export function discountRuleForTier(
  rules: CarrierLabelDiscountRule[],
  tierId: string | null
): CarrierLabelDiscountRule | null {
  return configuredRuleForScope(rules, {
    source: tierId ? "tier" : "default",
    tierId,
  });
}

export function customerOverrideRule(
  rules: CarrierLabelDiscountRule[],
  customerId: string,
  tierId: string | null
): CarrierLabelDiscountRule | null {
  return configuredRuleForScope(rules, {
    source: "override",
    customerId,
    tierId,
  });
}

type DiscountRuleScope = {
  source: "override" | "tier" | "default";
  customerId?: string | null;
  tierId?: string | null;
};

function configuredRuleForScope(
  rules: CarrierLabelDiscountRule[],
  scope: DiscountRuleScope,
  now = Date.now()
): CarrierLabelDiscountRule | null {
  const matches = rules.filter((rule) =>
    rule.eligibilityRows.some((row) => eligibilityMatchesScope(row, scope))
  );
  const effective = matches.filter((rule) => isRuleEffective(rule, now));
  return (
    rankRulesLikePricing(effective)[0] ??
    rankConfiguredRules(matches)[0] ??
    null
  );
}

function eligibilityMatchesScope(
  row: CarrierLabelDiscountRule["eligibilityRows"][number],
  scope: DiscountRuleScope
): boolean {
  if (row.customerId != null && row.customerId !== scope.customerId) {
    return false;
  }
  if (
    row.carrierCustomerTierId != null &&
    row.carrierCustomerTierId !== scope.tierId
  ) {
    return false;
  }
  if (row.carrierServiceId != null || row.dispatchCenterId != null) {
    return false;
  }

  const source = row.customerId
    ? "override"
    : row.carrierCustomerTierId
      ? "tier"
      : "default";
  return source === scope.source;
}

function isRuleEffective(
  rule: CarrierLabelDiscountRule,
  now = Date.now()
): boolean {
  if (rule.status !== "ACTIVE" || rule.percentage <= 0) return false;
  const startsAt = rule.startsAt ? new Date(rule.startsAt).getTime() : null;
  const endsAt = rule.endsAt ? new Date(rule.endsAt).getTime() : null;
  return (
    (startsAt == null || (Number.isFinite(startsAt) && startsAt <= now)) &&
    (endsAt == null || (Number.isFinite(endsAt) && endsAt > now))
  );
}

function rankRulesLikePricing(
  rules: CarrierLabelDiscountRule[]
): CarrierLabelDiscountRule[] {
  return [...rules].sort((a, b) => {
    if (a.percentage !== b.percentage) return b.percentage - a.percentage;
    const createdDelta = a.createdAt.localeCompare(b.createdAt);
    if (createdDelta !== 0) return createdDelta;
    return a.ruleId.localeCompare(b.ruleId);
  });
}

function rankConfiguredRules(
  rules: CarrierLabelDiscountRule[]
): CarrierLabelDiscountRule[] {
  const statusRank: Record<CarrierDiscountRuleStatus, number> = {
    ACTIVE: 3,
    DRAFT: 2,
    ARCHIVED: 1,
  };
  return [...rules].sort((a, b) => {
    const statusDelta = statusRank[b.status] - statusRank[a.status];
    if (statusDelta !== 0) return statusDelta;
    return rankRulesLikePricing([a, b])[0] === a ? -1 : 1;
  });
}

export function ruleEffectiveStatus(
  rule: CarrierLabelDiscountRule | null,
  now = Date.now()
): "ACTIVE" | "SCHEDULED" | "EXPIRED" | "DRAFT" | "ARCHIVED" | "NONE" {
  if (!rule) return "NONE";
  if (rule.status !== "ACTIVE") return rule.status;
  const startsAt = rule.startsAt ? new Date(rule.startsAt).getTime() : null;
  const endsAt = rule.endsAt ? new Date(rule.endsAt).getTime() : null;
  if (startsAt != null && Number.isFinite(startsAt) && startsAt > now) {
    return "SCHEDULED";
  }
  if (endsAt != null && Number.isFinite(endsAt) && endsAt < now) {
    return "EXPIRED";
  }
  return "ACTIVE";
}
